import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, Plus, Trash2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, PERMISSION_LABELS as PERM_BASE, PERMISSION_FLAGS as FLAG_BASE } from "@/lib/labels";
import { CRM_FLAGS, CRM_FLAGS_NOTE, FLAG_AGENDE, FLAG_SEDE_ALTRUI, FLAG_ELIMINA_TODO, FLAG_PASSAGGIO } from "@/lib/ambito";

// Sprint CRM-1 — quattro caselle CRM: assenti = spente, nessun valore acceso di partenza
const PERMISSION_LABELS = {
  ...PERM_BASE,
  crm_gestisci_pipeline: "CRM: gestisci pipeline",
  crm_gestisci_fasi: "CRM: gestisci fasi",
  crm_gestisci_elenchi: "CRM: gestisci elenchi",
  crm_elimina_opportunita: "CRM: elimina opportunità",
  crm_modifica_note_proprie: "CRM: modifica note proprie",
  crm_elimina_note_proprie: "CRM: elimina note proprie",
  crm_modifica_note_altrui: "CRM: modifica note altrui",
  crm_elimina_note_altrui: "CRM: elimina note altrui",
  gestione_config_passaggio: "Configura passaggio di proprietà",
  calendario_agende_colleghi: "Calendario: agende dei colleghi",
  calendario_gestisci_sede_altrui: "Calendario: gestisci eventi di sede altrui",
  calendario_elimina_todo: "Calendario: elimina To do List",  // Sprint CAL-1
};
import { useDoubleConfirm } from "@/components/ConfirmProvider";
import AddressItalyFields from "@/components/AddressItalyFields";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import ComunePicker from "@/components/ComunePicker";
import NazionePicker from "@/components/NazionePicker";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import DocumentsTab from "@/components/DocumentsTab";

const ROLE_VALUES = ["admin", "amministrazione", "back_office", "seller"];

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const confirm = useDoubleConfirm();
  const [u, setU] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [sitesByCompany, setSitesByCompany] = useState({});
  const [pwd, setPwd] = useState("");
  const [permOpen, setPermOpen] = useState(false);
  const [newPerm, setNewPerm] = useState({ company_id: "", site_id: "_all_", visualizza: true });

  const load = async () => {
    try {
      const r = await api.get(`/users/${id}`);
      const x = r.data;
      x.permissions_company_site = x.permissions_company_site || [];
      setU(x);
      const cs = await api.get("/companies");
      setCompanies(cs.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Sprint 2B.2-B (audit fix #2): pre-fetch sites per ogni permission row in
  // useEffect invece di chiamare loadSites() durante il render della map.
  useEffect(() => {
    const rows = u?.permissions_company_site || [];
    const missing = [...new Set(
      rows.map((p) => p.company_id).filter((cid) => cid && !sitesByCompany[cid])
    )];
    if (missing.length === 0) return;
    Promise.all(
      missing.map((cid) =>
        api.get(`/companies/${cid}/sites`).then((r) => [cid, r.data]).catch(() => [cid, []])
      )
    ).then((pairs) => {
      setSitesByCompany((m) => {
        const next = { ...m };
        for (const [cid, list] of pairs) next[cid] = list;
        return next;
      });
    });
    // eslint-disable-next-line
  }, [u?.permissions_company_site]);

  const loadSites = async (companyId) => {
    if (sitesByCompany[companyId]) return sitesByCompany[companyId];
    try {
      const r = await api.get(`/companies/${companyId}/sites`);
      setSitesByCompany((m) => ({ ...m, [companyId]: r.data }));
      return r.data;
    } catch { return []; }
  };

  const save = async () => {
    try {
      const payload = {
        first_name: u.first_name, last_name: u.last_name,
        email: u.email,
        role_default: u.role_default, is_active: u.is_active,
        is_seller: !!u.is_seller,
        provvigione_perc_default: u.provvigione_perc_default,
        permissions_company_site: u.permissions_company_site,
        // Sprint UTENTI-2B: dati anagrafici / fiscali estesi
        codice_fiscale: u.codice_fiscale ?? null,
        partita_iva: u.partita_iva ?? null,
        telefono: u.telefono ?? null,
        data_nascita: u.data_nascita ?? null,
        sesso: u.sesso ?? null,
        citta_nascita: u.citta_nascita ?? null,
        citta_nascita_estera: u.citta_nascita_estera ?? null,
        provincia_nascita: u.provincia_nascita ?? null,
        nazione_nascita: u.nazione_nascita ?? null,
        indirizzo: u.indirizzo ?? null,
        iva_codice: u.iva_codice ?? null,
        cod_sdi: u.cod_sdi ?? null,
        riferimento_amministrativo: u.riferimento_amministrativo ?? null,
        sconto_fisso: u.sconto_fisso ?? null,
        fatturato_annuo: u.fatturato_annuo ?? null,
        numero_dipendenti: u.numero_dipendenti ?? null,
        note: u.note ?? null,
      };
      if (pwd) payload.password = pwd;
      await api.patch(`/users/${id}`, payload);
      toast.success("Salvato");
      setPwd("");
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      // Errore strutturato email_already_used
      if (det && typeof det === "object" && det.error === "email_already_used") {
        toast.error(det.message || "Email già usata");
      } else {
        toast.error(typeof det === "string" ? det : "Errore");
      }
    }
  };

  const removeUser = async () => {
    const ok = await confirm({
      variant: "delete", title: "Elimina utente",
      message: `Stai per eliminare ${u.first_name} ${u.last_name} (${u.email}). L'utente sarà disattivato.`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    const motivo = window.prompt("Motivo (obbligatorio):") || "";
    if (!motivo.trim()) { toast.error("Motivo obbligatorio"); return; }
    try {
      await api.delete(`/users/${id}`, { data: { motivo } });
      toast.success("Utente eliminato");
      nav("/users");
    } catch (e) {
      const det = e?.response?.data?.detail;
      // Delete-guard: utente collegato a vendite (UTENTI-1)
      if (det && typeof det === "object" && det.error === "user_has_business_refs") {
        toast.error(det.message, { duration: 8000 });
        const disable = await confirm({
          variant: "default",
          title: "Disattivare invece?",
          message: `${u.first_name} ${u.last_name} è collegato a ${det.vendite_count} vendita/e. ` +
                   "L'eliminazione non è consentita, ma puoi disattivarlo.",
          confirmLabel: "Disattiva",
        });
        if (disable) {
          try {
            await api.patch(`/users/${id}`, { is_active: false });
            toast.success("Utente disattivato");
            load();
          } catch (er) {
            toast.error(er?.response?.data?.detail || "Errore");
          }
        }
        return;
      }
      toast.error(typeof det === "string" ? det : "Errore");
    }
  };

  const setPerm = (idx, key, val) => {
    const next = [...u.permissions_company_site];
    next[idx] = { ...next[idx], [key]: val };
    setU({ ...u, permissions_company_site: next });
  };

  const removePerm = (idx) => {
    const next = u.permissions_company_site.filter((_, i) => i !== idx);
    setU({ ...u, permissions_company_site: next });
  };

  const addPerm = () => {
    if (!newPerm.company_id) { toast.error("Seleziona azienda"); return; }
    const exists = u.permissions_company_site.some(
      (p) => p.company_id === newPerm.company_id && (p.site_id || null) === (newPerm.site_id === "_all_" ? null : newPerm.site_id),
    );
    if (exists) { toast.error("Combinazione già presente"); return; }
    const row = {
      company_id: newPerm.company_id,
      site_id: newPerm.site_id === "_all_" ? null : newPerm.site_id,
      visualizza: true,
      gestisci_impegno_contratto: false,
      crea_elimina_utenti: false,
      elimina_sostituisci_documenti: false,
      gestione_incassi: false,
      agire_su_pratica_venduta: false,
      agire_su_pratica_consegnata: false,
      vendita_sotto_margine: false,
      ...Object.fromEntries([...CRM_FLAGS, ...CRM_FLAGS_NOTE].map((f) => [f, false])),
    };
    setU({ ...u, permissions_company_site: [...u.permissions_company_site, row] });
    setPermOpen(false);
    setNewPerm({ company_id: "", site_id: "_all_", visualizza: true });
  };

  if (!u) return <div className="text-slate-500">Caricamento...</div>;

  const findCompany = (cid) => companies.find((c) => c.id === cid);
  const findSiteName = (cid, sid) => {
    if (!sid) return "Tutte le sedi";
    return (sitesByCompany[cid] || []).find((s) => s.id === sid)?.nome || sid;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/users" className="text-slate-500 hover:text-slate-700"><ChevronLeft className="h-5 w-5" /></Link>
          <UserIcon className="h-6 w-6 text-blue-600" />
          <h1 className="font-heading text-2xl font-semibold">{u.first_name} {u.last_name}</h1>
          <span className="text-sm text-slate-500">{u.email}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={removeUser} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="user-delete-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
          </Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="user-save-btn">Salva</Button>
        </div>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="bg-white border border-slate-200 mb-4 flex flex-wrap h-auto">
          <TabsTrigger value="account" data-testid="user-tab-account">Accesso e ruoli</TabsTrigger>
          <TabsTrigger value="anag" data-testid="user-tab-anag">Dati anagrafici</TabsTrigger>
          <TabsTrigger value="econ" data-testid="user-tab-econ">Dati economici / Fatt. elettronica</TabsTrigger>
          <TabsTrigger value="perm" data-testid="user-tab-perm">Permessi</TabsTrigger>
          <TabsTrigger value="docs" data-testid="user-tab-docs">Documenti</TabsTrigger>
        </TabsList>

        {/* ----------- TAB: ACCESSO E RUOLI (esistente) ----------- */}
        <TabsContent value="account" className="space-y-6">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <h2 className="font-heading text-base font-semibold mb-4">Accesso e identità</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome</Label>
                <Input value={u.first_name || ""} onChange={(e) => setU({ ...u, first_name: e.target.value })} /></div>
              <div><Label className="text-xs">Cognome</Label>
                <Input value={u.last_name || ""} onChange={(e) => setU({ ...u, last_name: e.target.value })} /></div>
              <div><Label className="text-xs">Email (login)</Label>
                <Input type="email" value={u.email || ""}
                       onChange={(e) => setU({ ...u, email: e.target.value })}
                       data-testid="user-detail-email" /></div>
              <div><Label className="text-xs">Nuova password (lascia vuoto per non cambiare)</Label>
                <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="min 10, 1 maiuscola, 1 minuscola, 1 numero" /></div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2 pt-2 border-t">
                <Switch checked={!!u.is_active} onCheckedChange={(v) => setU({ ...u, is_active: v })}
                        data-testid="user-detail-active" /> Utente attivo
              </label>
              {u.must_change_password && (
                <div className="sm:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  ⚠ Questo utente deve cambiare la password al prossimo accesso.
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <h2 className="font-heading text-base font-semibold mb-4">Ruolo e provvigione</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Ruolo</Label>
                <Select value={u.role_default} onValueChange={(v) => setU({ ...u, role_default: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_VALUES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    {u.role_default === "developer" && <SelectItem value="developer" disabled>Sviluppatore</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm h-10">
                  <Switch checked={!!u.is_seller} onCheckedChange={(v) => setU({ ...u, is_seller: v })} /> È venditore
                </label>
              </div>
              <div><Label className="text-xs">Provvigione default %</Label>
                <Input type="number" step="0.01" min="0" max="1"
                       value={u.provvigione_perc_default ?? 0.20}
                       onChange={(e) => setU({ ...u, provvigione_perc_default: parseFloat(e.target.value) })} />
                <div className="text-[10px] text-slate-400 mt-0.5">es. 0.20 = 20%</div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ----------- TAB: DATI ANAGRAFICI (NUOVO) ----------- */}
        <TabsContent value="anag" className="space-y-6">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white" data-testid="user-anag-section">
            <h2 className="font-heading text-base font-semibold mb-4">Dati anagrafici e fiscali</h2>
            <p className="text-xs text-slate-500 mb-4">
              Campi opzionali. Compilare per utenti che fatturano provvigioni o
              per cui servono dati di contatto/identificazione.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-4">
                <Label className="text-xs">Codice Fiscale</Label>
                <Input value={u.codice_fiscale || ""} maxLength={16}
                       onChange={(e) => setU({ ...u, codice_fiscale: e.target.value.toUpperCase() })}
                       className="font-mono uppercase" data-testid="user-cf" />
              </div>
              <div className="sm:col-span-4">
                <Label className="text-xs">Partita IVA</Label>
                <Input value={u.partita_iva || ""} maxLength={11}
                       onChange={(e) => setU({ ...u, partita_iva: e.target.value })}
                       className="font-mono" data-testid="user-piva" />
              </div>
              <div className="sm:col-span-4">
                <Label className="text-xs">Telefono</Label>
                <Input value={u.telefono || ""}
                       onChange={(e) => setU({ ...u, telefono: e.target.value })}
                       data-testid="user-tel" />
              </div>

              <div className="sm:col-span-3">
                <Label className="text-xs">Data nascita</Label>
                <Input type="date" value={u.data_nascita || ""}
                       onChange={(e) => setU({ ...u, data_nascita: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Sesso</Label>
                <Select value={u.sesso || ""} onValueChange={(v) => setU({ ...u, sesso: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">M</SelectItem>
                    <SelectItem value="F">F</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-4">
                <Label className="text-xs">Nazione di nascita</Label>
                <NazionePicker value={u.nazione_nascita || "IT"}
                               onChange={(code, naz) => {
                                 const patch = { nazione_nascita: code || "IT" };
                                 if (code && code !== "IT") {
                                   patch.citta_nascita = naz?.nome || "";
                                   patch.provincia_nascita = "EE";
                                 }
                                 setU({ ...u, ...patch });
                               }}
                               testId="user-nazione-nascita" />
              </div>
              {(u.nazione_nascita || "IT") === "IT" ? (
                <>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Comune nascita</Label>
                    <ComunePicker value={u.citta_nascita || ""}
                                  provinciaFilter={u.provincia_nascita || ""}
                                  onChange={(nome, comune) => {
                                    const patch = { citta_nascita: nome || "" };
                                    if (comune?.provincia_sigla) patch.provincia_nascita = comune.provincia_sigla;
                                    setU({ ...u, ...patch });
                                  }}
                                  testId="user-citta-nascita" />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Prov.</Label>
                    <ProvinciaPicker value={u.provincia_nascita || ""}
                                     onChange={(v) => setU({ ...u, provincia_nascita: (v || "").toUpperCase().slice(0, 2) })}
                                     testId="user-prov-nascita" />
                  </div>
                </>
              ) : (
                <div className="sm:col-span-3">
                  <Label className="text-xs">Località estera</Label>
                  <Input value={u.citta_nascita_estera || ""}
                         onChange={(e) => setU({ ...u, citta_nascita_estera: e.target.value })} />
                </div>
              )}

              <div className="sm:col-span-12 pt-3 border-t">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Indirizzo (Italia)</Label>
                <AddressItalyFields
                  value={u.indirizzo || {}}
                  onChange={(addr) => setU({ ...u, indirizzo: addr })}
                  testIdPrefix="user-addr"
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ----------- TAB: DATI ECONOMICI / FATT. ELETTRONICA (NUOVO) ----------- */}
        <TabsContent value="econ" className="space-y-6">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white" data-testid="user-econ-section">
            <h2 className="font-heading text-base font-semibold mb-4">Dati economici</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Codice IVA (% default)</Label>
                <CodiceIvaPicker
                  value={u.iva_codice || null}
                  onChange={(v) => setU({ ...u, iva_codice: v })}
                  testId="user-iva-codice"
                  placeholder="Codice IVA…"
                />
              </div>
              <div>
                <Label className="text-xs">Sconto fisso (%)</Label>
                <Input type="number" step="0.01" min="0" value={u.sconto_fisso ?? ""}
                       onChange={(e) => setU({ ...u, sconto_fisso: e.target.value ? parseFloat(e.target.value) : null })} />
              </div>
              <div>
                <Label className="text-xs">Numero dipendenti</Label>
                <Input type="number" min="0" value={u.numero_dipendenti ?? ""}
                       onChange={(e) => setU({ ...u, numero_dipendenti: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Fatturato annuo (€)</Label>
                <Input type="number" step="0.01" min="0" value={u.fatturato_annuo ?? ""}
                       onChange={(e) => setU({ ...u, fatturato_annuo: e.target.value ? parseFloat(e.target.value) : null })} />
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <h2 className="font-heading text-base font-semibold mb-4">Fatturazione elettronica</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Codice SDI (7 caratteri)</Label>
                <Input value={u.cod_sdi || ""} maxLength={7}
                       className="font-mono uppercase"
                       onChange={(e) => setU({ ...u, cod_sdi: e.target.value.toUpperCase() })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Riferimento amministrativo</Label>
                <Input value={u.riferimento_amministrativo || ""}
                       onChange={(e) => setU({ ...u, riferimento_amministrativo: e.target.value })} />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* ----------- TAB: PERMESSI (esistente) ----------- */}
        <TabsContent value="perm" className="space-y-6">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-heading text-base font-semibold">Permessi azienda × sede</h2>
              <Button size="sm" onClick={() => setPermOpen(true)} data-testid="perm-add-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
            </div>
            {(u.permissions_company_site || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">Nessun permesso. {u.role_default === "developer" ? "Lo sviluppatore vede tutto." : ""}</div>
            ) : (
              <div className="space-y-3">
                {u.permissions_company_site.map((p, idx) => {
                  const c = findCompany(p.company_id);
                  return (
                    <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                        <div className="font-medium text-sm">
                          {c?.nome_breve || c?.name || "?"} <span className="text-slate-500 font-normal">— {findSiteName(p.company_id, p.site_id)}</span>
                        </div>
                        <button onClick={() => removePerm(idx)} className="text-rose-600 hover:text-rose-700 text-xs flex items-center gap-1" data-testid={`perm-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Rimuovi
                        </button>
                      </div>
                      {/* Sprint CRM-5 — caselle raggruppate per argomento; nessuna cambia significato */}
                      {/* Sprint CRM-6 — «Configura passaggio di proprietà» (casella già esistente) e le quattro caselle delle note */}
                      {[["Gestionale", [...FLAG_BASE.filter((f) => f !== FLAG_PASSAGGIO), FLAG_PASSAGGIO]], ["CRM", [...CRM_FLAGS, ...CRM_FLAGS_NOTE]], ["Calendario", [FLAG_AGENDE, FLAG_SEDE_ALTRUI, FLAG_ELIMINA_TODO]]].map(([gruppo, flags]) => (
                        <div key={gruppo} className="space-y-1" data-testid={`perm-gruppo-${idx}-${gruppo.toLowerCase()}`}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{gruppo}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {flags.map((flag) => (
                              <label key={flag} className="flex items-center gap-2 text-xs">
                                <Checkbox checked={!!p[flag]} onCheckedChange={(v) => setPerm(idx, flag, !!v)} data-testid={`perm-flag-${idx}-${flag}`} />
                                {PERMISSION_LABELS[flag]}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <details>
              <summary className="cursor-pointer font-heading text-base font-semibold">Override permessi fase</summary>
              <div className="mt-3 text-sm text-slate-500 italic">Le azioni di fase saranno disponibili da Sprint 5.</div>
            </details>
          </Card>
        </TabsContent>

        {/* ----------- TAB: DOCUMENTI (NUOVO) ----------- */}
        <TabsContent value="docs" className="space-y-3">
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <div className="mb-3">
              <h2 className="font-heading text-base font-semibold">Documenti utente</h2>
              <p className="text-xs text-slate-500">
                Contratto licenza, privacy, documenti personali. Storage dedicato
                separato dalle aziende (cartella <code className="font-mono text-[10px]">_users/</code>).
                Accesso riservato a developer/admin.
              </p>
            </div>
            <DocumentsTab ownerType="user" ownerId={u.id} companyId={null}
                          canUpload={true} canDelete={true} />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add permission dialog */}
      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiungi permesso azienda × sede</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Azienda</Label>
              <Select value={newPerm.company_id} onValueChange={async (v) => { setNewPerm({ ...newPerm, company_id: v, site_id: "_all_" }); await loadSites(v); }}>
                <SelectTrigger data-testid="perm-add-company"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_breve || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newPerm.company_id && (
              <div><Label className="text-xs">Sede</Label>
                <Select value={newPerm.site_id} onValueChange={(v) => setNewPerm({ ...newPerm, site_id: v })}>
                  <SelectTrigger data-testid="perm-add-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Tutte le sedi</SelectItem>
                    {(sitesByCompany[newPerm.company_id] || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermOpen(false)}>Annulla</Button>
            <Button onClick={addPerm} data-testid="perm-add-confirm">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
